import React, { useEffect, useState } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from "chart.js";
import "../styles/AdminDashBoard.css";
import axiosInstance from "../api/api.js";
import axios from "axios";
import { DateTime } from "luxon";

Chart.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

const AdminDashboard = () => {
  const getStatusMetrics = (key) => {
    const metrics = {
      currentlyOnline: {
        label: "Currently Online",
        color: "#1e3a8a",
        bgColor: "#dbeafe",
        icon: "●",
      },
      lateClockin: {
        label: "Late Clock-in",
        color: "#ca8a04",
        bgColor: "#fef3c7",
        icon: "⏱",
      },
      onLeave: {
        label: "On Leave",
        color: "#1e3a8a",
        bgColor: "#dbeafe",
        icon: "📅",
      },
      absent: {
        label: "Absent",
        color: "#991b1b",
        bgColor: "#fee2e2",
        icon: "✕",
      },
      earlyClockout: {
        label: "Early Clock-out",
        color: "#ca8a04",
        bgColor: "#fef3c7",
        icon: "⏰",
      },
      overtimeWorking: {
        label: "Overtime Working",
        color: "#1e3a8a",
        bgColor: "#dbeafe",
        icon: "⚡",
      },
      totalOutsource: {
        label: "Total Outsource",
        color: "#7c3aed",
        bgColor: "#ede9fe",
        icon: "👥",
      },
      onlineOutsource: {
        label: "Online Outsource",
        color: "#059669",
        bgColor: "#d1fae5",
        icon: "🟢",
      },
    };
    return (
      metrics[key] || {
        label: key,
        color: "#6b7280",
        bgColor: "#f3f4f6",
        icon: "■",
      }
    );
  };

  const getAdjustmentTimeColor = (adjustedTime) => {
    const now = DateTime.now().setZone("Europe/London");
    const adjustmentDate = DateTime.fromISO(adjustedTime, { zone: "Europe/London" });
    const hoursDiff = (now - adjustmentDate) / (1000 * 60 * 60);

    if (hoursDiff < 24) return "#1e3a8a"; // Less than 24 hours ago
    if (hoursDiff < 168) return "#ca8a04"; // Less than 1 week ago
    return "#6b7280"; // Older than 1 week
  };

  const getTimeBadgeClass = (adjustedTime) => {
    const now = DateTime.now().setZone("Europe/London");
    const adjustmentDate = DateTime.fromISO(adjustedTime, { zone: "Europe/London" });
    const hoursDiff = (now - adjustmentDate) / (1000 * 60 * 60);

    if (hoursDiff < 24) return "recent";
    if (hoursDiff < 168) return "recent-week";
    return "older";
  };

  const formatDateOnly = (dateString) => {
    if (!dateString || dateString === "No Date") return "No date";
    const date = DateTime.fromISO(dateString, { zone: "Europe/London" });
    return date.toFormat("dd LLL yyyy"); // e.g., 20 Aug 2025
  };


  const [availability, setAvailability] = useState({});
  const [adjustments, setAdjustments] = useState([]);
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLateModal, setShowLateModal] = useState(false);
  const [lateList, setLateList] = useState([]);
  const [showEarlyModal, setShowEarlyModal] = useState(false);
  const [earlyList, setEarlyList] = useState([]);
  const [showTotalOutsourceModal, setShowTotalOutsourceModal] = useState(false);
  const [totalOutsourceList, setTotalOutsourceList] = useState([]);
  const [showOnlineOutsourceModal, setShowOnlineOutsourceModal] = useState(false);
  const [onlineOutsourceList, setOnlineOutsourceList] = useState([]);
  const [viewAllAdjustments, setViewAllAdjustments] = useState(false);
  const [stats, setStats] = useState({
    totalEmployees: 0,
    attendanceRate: 0,
    onTimeRate: 0,
  });

  const fetchModalList = async (type) => {
    try {
      if (type === "late") {
        const res = await axiosInstance.get("/admin-dashboard/late-clockin-list");
        console.log("Late clock-in list data:", res.data);
        setLateList(Array.isArray(res.data) ? res.data : []);
      } else if (type === "early") {
        const res = await axiosInstance.get("/admin-dashboard/early-clockout-list");
        console.log("Early clock-out list data:", res.data);
        setEarlyList(Array.isArray(res.data) ? res.data : []);
      } else if (type === "totalOutsource") {
        const res = await axiosInstance.get("/admin-dashboard/total-outsource-list");
        console.log("Total outsource list data:", res.data);
        setTotalOutsourceList(Array.isArray(res.data) ? res.data : []);
      } else if (type === "onlineOutsource") {
        const res = await axiosInstance.get("/admin-dashboard/online-outsource-list");
        console.log("Online outsource list data:", res.data);
        setOnlineOutsourceList(Array.isArray(res.data) ? res.data : []);
      }
    } catch (err) {
      console.error(err);
      if (type === "late") setLateList([]);
      else if (type === "early") setEarlyList([]);
      else if (type === "totalOutsource") setTotalOutsourceList([]);
      else if (type === "onlineOutsource") setOnlineOutsourceList([]);
    }
  };

  useEffect(() => {
    if (showLateModal) fetchModalList("late");
  }, [showLateModal]);

  useEffect(() => {
    if (showEarlyModal) fetchModalList("early");
  }, [showEarlyModal]);

  useEffect(() => {
    if (showTotalOutsourceModal) fetchModalList("totalOutsource");
  }, [showTotalOutsourceModal]);

  useEffect(() => {
    if (showOnlineOutsourceModal) fetchModalList("onlineOutsource");
  }, [showOnlineOutsourceModal]);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        const [availabilityRes, statsRes, adjustmentsRes, chartRes] =
          await Promise.all([
            axiosInstance.get("/admin-dashboard/availability"),
            axiosInstance.get("/admin-dashboard/stats"),
            axiosInstance.get("/admin-dashboard/adjustments"),
            axiosInstance.get("/admin-dashboard/attendance-chart"),
          ]);

        setAvailability(availabilityRes.data);
        setAdjustments(adjustmentsRes.data);

        const labels = chartRes.data.map((d) => d.date);
        setChartData({
          labels,
          datasets: [
            {
              label: "Present",
              backgroundColor: "#1e3a8a",
              hoverBackgroundColor: "#1e40af",
              borderWidth: 0,
              data: chartRes.data.map((d) => d.present),
            },
            {
              label: "Absent",
              backgroundColor: "#ca8a04",
              hoverBackgroundColor: "#a16207",
              borderWidth: 0,
              data: chartRes.data.map((d) => d.absent),
            },
          ],
        });

        setStats({
          totalEmployees: statsRes.data.totalEmployees,
          attendanceRate: statsRes.data.attendanceRate,
          onTimeRate: statsRes.data.onTimeRate,
        });
      } catch (error) {
        console.error("Failed to load dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  if (loading) {
    return (
      <div className="admin-dashboard-loading">
        <div className="dashboard-container">
          <div className="loading-header"></div>
          <div className="loading-grid">
            <div className="loading-card"></div>
            <div className="loading-card"></div>
            <div className="loading-card"></div>
          </div>
        </div>
      </div>
    );
  }

  if (viewAllAdjustments) {
    return (
      <div className="admin-dashboard">
        <div className="dashboard-container">
          <div className="full-adjustments-view">
            <div className="dashboard-card">
              <div className="card-header">
                <div className="adjustments-header">
                  <div>
                    <h2 className="card-title">All Manual Adjustments</h2>
                    <p className="card-subtitle">
                      Complete history of attendance modifications
                    </p>
                  </div>
                  <button
                    className="back-button"
                    onClick={() => setViewAllAdjustments(false)}
                  >
                    ← Back to Dashboard
                  </button>
                </div>
              </div>

              <div className="card-content">
                <div className="adjustments-table-container">
                  <table className="adjustments-table">
                    <thead>
                      <tr>
                        <th>Edit By</th>
                        <th>Attendance Date</th>
                        <th>Adjustment Date</th>
                        <th>Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {adjustments.length === 0 ? (
                        <tr>
                          <td colSpan="4" className="no-data">
                            No adjustments found
                          </td>
                        </tr>
                      ) : (
                        adjustments.map((adj) => (
                          <tr key={adj.id} className="adjustment-row">
                            <td>{adj.employeeName}</td>
                            <td>{formatDateOnly(adj.date)}</td>
                            <td>{formatDateOnly(adj.adjusted_time)}</td>
                            <td>{adj.reason}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <div className="dashboard-container">
        <div className="dashboard-grid">
          <div className="availability-section">
            <div className="dashboard-card">
              <div className="card-header">
                <h2 className="card-title">Employee Status Overview</h2>
                <p className="card-subtitle">
                  Real-time employee availability status
                </p>
              </div>
              <div className="card-content">
                <div className="status-grid">
                  {Object.entries(availability).map(([key, count]) => {
                    const metrics = getStatusMetrics(key);
                    return (
                      <div
                        key={key}
                        className="status-card"
                        style={{
                          backgroundColor: metrics.bgColor,
                          cursor: key === 'lateClockin' || key === 'earlyClockout' || key === 'totalOutsource' || key === 'onlineOutsource' ? 'pointer' : 'default'
                        }}
                        onClick={() => {
                          if (key === 'lateClockin') setShowLateModal(true);
                          if (key === 'earlyClockout') setShowEarlyModal(true);
                          if (key === 'totalOutsource') setShowTotalOutsourceModal(true);
                          if (key === 'onlineOutsource') setShowOnlineOutsourceModal(true);
                        }}
                      >
                        <div className="status-icon" style={{ color: metrics.color }}>
                          {metrics.icon}
                        </div>
                        <span className="status-count">{count}</span>
                        <p className="status-label">{metrics.label}</p>
                      </div>
                    );
                  })}

                </div>
              </div>
            </div>
          </div>

          <div className="stats-section">
            <div className="dashboard-card">
              <div className="card-header">
                <p className="current-date">
                  {new Date().toLocaleDateString("en-US", {
                    weekday: "long",
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </p>
                <p className="card-subtitle">Key metrics for today</p>
              </div>
              <div className="card-content">
                <div className="stat-item">
                  <span className="stat-label">Total Employees</span>
                  <span className="stat-value">{stats.totalEmployees}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Attendance Rate</span>
                  <span className="stat-value text-green">
                    {stats.attendanceRate}%
                  </span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">On Time Rate</span>
                  <span className="stat-value text-blue">
                    {stats.onTimeRate}%
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="adjustments-section">
          <div className="dashboard-card">
            <div className="card-header">
              <div className="adjustments-header">
                <div>
                  <h2 className="card-title">Recent Adjustments</h2>
                  <p className="card-subtitle">
                    Latest manual attendance modifications
                  </p>
                </div>
                <button
                  className="view-all-button"
                  onClick={() => setViewAllAdjustments(true)}
                >
                  View All
                </button>
              </div>
            </div>

            <div className="card-content">
              <div className="adjustments-list">
                {adjustments.slice(0, 4).map((adj) => (
                  <div key={adj.id} className="adjustment-item">
                    <div
                      className="employee-avatar"
                      style={{
                        backgroundColor: `${getAdjustmentTimeColor(
                          adj.adjusted_time
                        )}20`,
                      }}
                      title={adj.employeeName}
                    >
                      {adj.employeeName
                        .split(" ")
                        .map((n) => n[0])
                        .join("")
                        .toUpperCase()}
                    </div>

                    <div className="adjustment-details">
                      <div className="adjustment-header">
                        <p className="employee-name">{adj.employeeName}</p>
                        <span className="adjustment-date">
                          {formatDateOnly(adj.date)}
                        </span>
                      </div>

                      <p className="adjustment-reason">{adj.reason}</p>

                      <div
                        className={`adjustment-tag ${getTimeBadgeClass(
                          adj.adjusted_time
                        )}`}
                        style={{
                          backgroundColor: `${getAdjustmentTimeColor(
                            adj.adjusted_time
                          )}15`,
                          color: getAdjustmentTimeColor(adj.adjusted_time),
                        }}
                      >
                        {formatDateOnly(adj.adjusted_time)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="dashboard-card">
            <div className="card-header">
              <h2 className="card-title">7-Day Attendance Trend</h2>
              <p className="card-subtitle">Daily attendance overview</p>
            </div>
            <div className="card-content">
              {chartData ? (
                <div className="chart-wrapper">
                  <Bar
                    data={chartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          position: "bottom",
                          labels: {
                            color: "#374151",
                            font: {
                              size: 12,
                              weight: "500",
                            },
                            usePointStyle: true,
                            padding: 20,
                            boxWidth: 6,
                            boxHeight: 6,
                          },
                        },
                        tooltip: {
                          backgroundColor: "rgba(17, 24, 39, 0.95)",
                          titleColor: "#F9FAFB",
                          bodyColor: "#F3F4F6",
                          cornerRadius: 8,
                          padding: 12,
                          titleFont: { size: 13, weight: "600" },
                          bodyFont: { size: 12 },
                        },
                      },
                      scales: {
                        x: {
                          grid: {
                            display: false,
                          },
                          ticks: {
                            color: "#6B7280",
                            font: { size: 11, weight: "500" },
                          },
                        },
                        y: {
                          beginAtZero: true,
                          grid: {
                            color: "#F3F4F6",
                            drawBorder: false,
                          },
                          ticks: {
                            color: "#6B7280",
                            font: { size: 11, weight: "500" },
                          },
                        },
                      },
                      elements: {
                        bar: {
                          borderRadius: 4,
                          borderSkipped: false,
                        },
                      },
                    }}
                  />
                </div>
              ) : (
                <div className="chart-error">
                  <div className="error-icon">📊</div>
                  <div>Chart data unavailable</div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {showLateModal && (
        <div className="late-modal-overlay">
          <div className="late-modal-container">
            <div className="late-modal-header">
              <h2 className="late-modal-title">Late Clock-ins Today</h2>
              <button
                className="late-modal-close-btn"
                onClick={() => setShowLateModal(false)}
              >
                ✕
              </button>
            </div>

            <div className="late-modal-body">
              {Array.isArray(lateList) && lateList.length > 0 ? (
                lateList.map(user => (
                  <li key={user.id} className="late-modal-item">
                    <span className="late-user-name">{user.name}</span>
                    <span className="late-user-time">{user.clock_in}</span>
                  </li>
                ))
              ) : (
                <p className="late-modal-empty">No late clock-ins today</p>
              )}
            </div>
          </div>
        </div>
      )}
      {showEarlyModal && (
        <div className="late-modal-overlay">
          <div className="late-modal-container">
            <div className="late-modal-header">
              <h2 className="late-modal-title">Early Clock-outs Today</h2>
              <button
                className="late-modal-close-btn"
                onClick={() => setShowEarlyModal(false)}
              >
                ✕
              </button>
            </div>

            <div className="late-modal-body">
              {Array.isArray(earlyList) && earlyList.length > 0 ? (
                earlyList.map(user => (
                  <li key={user.id} className="late-modal-item">
                    <span className="late-user-name">{user.name}</span>
                    <span className="late-user-time">{user.clock_out}</span>
                  </li>
                ))
              ) : (
                <p className="late-modal-empty">No early clock-outs today</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Total Outsource Modal */}
      {showTotalOutsourceModal && (
        <div className="late-modal-overlay">
          <div className="late-modal-container">
            <div className="late-modal-header">
              <h2 className="late-modal-title">Total Outsource Employees</h2>
              <button
                className="late-modal-close-btn"
                onClick={() => setShowTotalOutsourceModal(false)}
              >
                ✕
              </button>
            </div>

            <div className="late-modal-body">
              {Array.isArray(totalOutsourceList) && totalOutsourceList.length > 0 ? (
                totalOutsourceList.map(employee => (
                  <li key={employee.id} className="late-modal-item">
                    <span className="late-user-name">{employee.name}</span>
                    <span className="late-user-email">{employee.email}</span>
                  </li>
                ))
              ) : (
                <p className="late-modal-empty">No outsource employees found</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Online Outsource Modal */}
      {showOnlineOutsourceModal && (
        <div className="late-modal-overlay">
          <div className="late-modal-container">
            <div className="late-modal-header">
              <h2 className="late-modal-title">Currently Online Outsource Employees</h2>
              <button
                className="late-modal-close-btn"
                onClick={() => setShowOnlineOutsourceModal(false)}
              >
                ✕
              </button>
            </div>

            <div className="late-modal-body">
              {Array.isArray(onlineOutsourceList) && onlineOutsourceList.length > 0 ? (
                onlineOutsourceList.map(employee => (
                  <li key={employee.id} className="late-modal-item">
                    <span className="late-user-name">{employee.name}</span>
                    <span className="late-user-time">{employee.clock_in}</span>
                  </li>
                ))
              ) : (
                <p className="late-modal-empty">No online outsource employees</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;